import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Milk, IndianRupee, Users, Sun, Moon, Clock } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-3))", "hsl(var(--chart-2))", "hsl(var(--chart-4))"];
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Dashboard() {
  const { tenant } = useAuth();
  const { t } = useI18n();
  const { data: stats, isLoading: l1 } = useQuery({ queryKey: ["dash-stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: charts, isLoading: l2 } = useQuery({ queryKey: ["dash-charts"], queryFn: async () => (await api.get("/dashboard/charts")).data });

  if (l1 || l2 || !stats || !charts)
    return (
      <div className="space-y-6">
        <PageHeader title={t("dash.title")} breadcrumb={[t("crumb.center"), t("dash.title")]} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-80" />
      </div>
    );

  const kpis = [
    { label: t("dash.todayMilk"), value: `${stats.today_milk} L`, icon: Milk, accent: "primary" },
    { label: t("dash.todayAmount"), value: money(stats.today_amount), icon: IndianRupee, accent: "primary" },
    { label: t("dash.todayFarmers"), value: stats.today_farmers, icon: Users, accent: "secondary" },
    { label: t("dash.morning"), value: `${stats.morning_collection} L`, icon: Sun, accent: "warning" },
    { label: t("dash.evening"), value: `${stats.evening_collection} L`, icon: Moon, accent: "secondary" },
    { label: t("dash.pending"), value: money(stats.pending_payments), icon: Clock, accent: "danger" },
    { label: t("dash.totalFarmers"), value: stats.total_farmers, icon: Users, accent: "primary" },
  ];

  return (
    <div className="space-y-6" data-testid="tenant-dashboard">
      <PageHeader title={t("dash.title")} subtitle={tenant?.center_name} breadcrumb={[t("crumb.center"), t("dash.title")]} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k, i) => <KpiCard key={k.label} {...k} index={i} testId={`kpi-${["milk","amount","farmers","morning","evening","pending","total"][i]}`} />)}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-lg">{t("dash.daily")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={charts.daily}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4} /><stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Area type="monotone" dataKey="quantity" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#g1)" name={t("dash.litres")} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">{t("dash.milkType")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={charts.milk_type} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {charts.milk_type.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">{t("dash.monthly")}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="quantity" name="Litres" radius={[5, 5, 0, 0]} fill="hsl(var(--chart-1))" />
                <Bar dataKey="amount" name="Amount ₹" radius={[5, 5, 0, 0]} fill="hsl(var(--chart-3))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Recent Collections</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {charts.recent_collections.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No collections yet.</p>}
            {charts.recent_collections.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
                <div>
                  <p className="font-medium">{c.farmer_name}</p>
                  <p className="text-xs text-muted-foreground">{c.date} · {c.shift} · {c.milk_type}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold tnum">{money(c.amount)}</p>
                  <Badge variant="outline" className="text-xs">{c.quantity} L</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
